/**
 * useOrderSSE — subscribes to Server-Sent Events for real-time order updates.
 *
 * Connects to backend-v3 /events/orders endpoint (JWT auth via URL param).
 * Automatically invalidates TanStack Query caches when an update arrives.
 *
 * Usage:
 *   useOrderSSE(); // Call once in the layout/root component
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { startTokenSSE } from './tokenSSE';
import { STORAGE_KEYS } from '../constants/storage-keys';

const V3_API_URL =
  process.env.REACT_APP_BACKEND_V3_URL || 'http://localhost:3001/api/v1';

const V2_SSE_URL = '/api/sse/events';

export interface OrderUpdateEvent {
  orderId: string;
  orderType: string;
  status: string;
  updatedAt: string;
}

function attachTsoftListeners(
  es: EventSource,
  qc: ReturnType<typeof useQueryClient>,
) {
  es.addEventListener('round.status', (e: MessageEvent) => {
    try {
      const d = JSON.parse(e.data);
      qc.invalidateQueries({ queryKey: ['rounds', d.roundId] });
      qc.invalidateQueries({ queryKey: ['rounds'] });
      qc.invalidateQueries({ queryKey: ['city-rounds', d.roundId] });
      qc.invalidateQueries({ queryKey: ['city-rounds'] });
    } catch { /* ignore malformed */ }
  });

  es.addEventListener('order.status', (e: MessageEvent) => {
    try {
      const d = JSON.parse(e.data);
      const route = `${d.orderType ?? 'regional'}-orders`;
      qc.invalidateQueries({ queryKey: [route, d.orderId] });
      qc.invalidateQueries({ queryKey: [route] });
    } catch { /* ignore malformed */ }
  });

  es.addEventListener('payment.created', (e: MessageEvent) => {
    try {
      const d = JSON.parse(e.data);
      qc.invalidateQueries({ queryKey: ['payments', d.orderId] });
    } catch { /* ignore malformed */ }
  });

  es.addEventListener('ping', () => {});
}

/** Connect to backend-v2 SSE channel for real-time dispatcher board updates */
export function useTsoftSSE() {
  const qc = useQueryClient();
  useEffect(() => startTokenSSE({
    url: V2_SSE_URL,
    withCredentials: true,
    readToken: () => localStorage.getItem(STORAGE_KEYS.TOKEN),
    authenticate: () => api.get('/auth/me', { timeout: 10000 }),
    attach: es => attachTsoftListeners(es, qc),
  }), [qc]);
}

export function useOrderSSE() {
  const qc = useQueryClient();
  useEffect(() => startTokenSSE({
    url: `${V3_API_URL}/events/orders`,
    readToken: () => localStorage.getItem(STORAGE_KEYS.TOKEN),
    authenticate: () => api.get('/auth/me', { timeout: 10000 }),
    attach: es => {
    es.addEventListener('order-updated', (e: MessageEvent) => {
      try {
        const update: OrderUpdateEvent = JSON.parse(e.data);
        const route = `${update.orderType}-orders`;
        qc.invalidateQueries({ queryKey: [route, update.orderId] });
        qc.invalidateQueries({ queryKey: [route] });
        qc.invalidateQueries({
          queryKey: ['financials', update.orderType, update.orderId],
        });
      } catch {
        // malformed event — ignore
      }
    });
    },
  }), [qc]);
}
